import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Chart, registerables, LayoutPosition } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables);
Chart.register(ChartDataLabels);

@Component({
	selector: 'app-doughnut-pie-chart-least-clicked',
	templateUrl: './doughnut-pie-chart-least-clicked.component.html',
	styleUrls: ['./doughnut-pie-chart-least-clicked.component.scss']
})
export class DoughnutPieChartLeastClickedComponent implements OnChanges, AfterViewInit, OnDestroy {
	private static idCounter = 0;

	@ViewChild('chartCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;
	@ViewChild('wrapper') wrapperRef?: ElementRef<HTMLElement>;

	private chart: Chart | null = null;
	private viewInitialized = false;
	private readonly generatedId = `least-clicked-chart-${++DoughnutPieChartLeastClickedComponent.idCounter}`;
	private resizeObserver?: ResizeObserver;
	private readonly isBrowser: boolean;

	constructor(public translate: TranslateService, @Inject(PLATFORM_ID) platformId: object) {
		this.isBrowser = isPlatformBrowser(platformId);
	}

	@Input() idChart?: string;
	@Input() labels: any[] = [];
	@Input() data: number[] = [];
	@Input() typeChart: any = 'doughnut';
	@Input() positionChart: LayoutPosition = 'right';

	get canvasId(): string {
		return this.idChart || this.generatedId;
	}

	ngAfterViewInit(): void {
		if (!this.isBrowser) {
			return;
		}
		this.viewInitialized = true;
		this.setupResizeObserver();
		this.handleInitChartjs();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.isBrowser) {
			return;
		}
		if (!this.viewInitialized) {
			return;
		}
		if (changes['data'] || changes['labels'] || changes['typeChart']) {
			this.handleInitChartjs();
		}
	}

	ngOnDestroy(): void {
		this.destroyChart();
		this.resizeObserver?.disconnect();
	}

	private handleInitChartjs(): void {
		if (!this.isBrowser) {
			return;
		}
		if (!Array.isArray(this.data) || !Array.isArray(this.labels) || !this.data.length) {
			this.destroyChart();
			return;
		}

		const canvas = this.canvasRef?.nativeElement;
		const context = canvas?.getContext('2d') ?? null;
		if (!context) {
			console.error('Failed to acquire 2D context for least-clicked chart');
			return;
		}

		this.destroyChart();

		// Ensure the canvas has a non-zero size at first render
		this.ensureCanvasSize();

		const customColors = [
			'#054659',
			'#F18700',
			'#2A77AD',
			'#F9B000',
			'#3BB93B',
			'#7460DD',
			'#0099C9',
			'#F0BE4B',
			'#61CE61',
			'#20829E',
			'#9487D3',
			'#FFAC41',
			'#50BEDF',
			'#B9DFB9',
			'#6952D9',
			'#0DA9D9',
			'#F29E2F',
			'#1CAE94',
			'#B47F03',
			'#3494D9',
			'#503FA6',
			'#73D973',
			'#D97B00',
			'#EBA70A',
			'#EBC70A'
		];

		try {
			this.chart = new Chart(context, {
				type: this.typeChart,
				data: {
					labels: [...this.labels],
					datasets: [
						{
							label: '',
							data: [...this.data],
							backgroundColor: customColors,
							borderColor: customColors,
							borderWidth: 0
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					cutout: '50%',
					plugins: {
						tooltip: {
							callbacks: {
								title: tooltipItems => {
									return tooltipItems[0].label;
								},
								label: tooltipItem => {
									const total = tooltipItem.dataset.data.reduce(
										(accumulator: number, currentValue: number) => accumulator + currentValue,
										0
									);
									const percentage =
										total === 0 ? 0 : Number(((Math.round(tooltipItem.parsed) / total) * 100).toFixed(0));
									return ' ' + `${percentage}%` + ' | ' + tooltipItem.raw + ' ' + this.translate.instant('clicks');
								},
								labelTextColor: tooltipItem => {
									return '#FFFFFF';
								}
							},
							bodyFont: {
								size: 12,
								weight: 'bold'
							},
							position: 'nearest',
							titleColor: '#FFFFFF',
							backgroundColor: '#262626',
							displayColors: true
						},
						legend: {
							display: false,
							position: this.positionChart,

							labels: {
								padding: 20,
								font: {
									size: 14,
									weight: '400',
									lineHeight: 22,
									family: 'ZT Nature'
								},
								usePointStyle: true,
								boxHeight: 7,
								pointStyleWidth: 10
							}
						},
						datalabels: {
							color: '#ffffff',
							font: {
								weight: '500',
								size: 16,
								lineHeight: 22
							}
						}
					}
				}
			});
		} catch (error) {
			console.warn('Skipping least-clicked chart render (SSR/DOM not supported).', error);
			this.destroyChart();
		}
	}

	private setupResizeObserver(): void {
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const wrapper = this.wrapperRef?.nativeElement;
		if (!wrapper) return;

		this.resizeObserver = new ResizeObserver(() => {
			if (this.chart) {
				this.chart.resize();
			} else if (this.viewInitialized) {
				this.handleInitChartjs();
			}
		});
		this.resizeObserver.observe(wrapper);
	}

	private ensureCanvasSize(): void {
		const wrapper = this.wrapperRef?.nativeElement;
		const canvas = this.canvasRef?.nativeElement;
		if (!wrapper || !canvas) return;
		const rect = wrapper.getBoundingClientRect();
		const fallback = 220;
		const width = Math.max(Math.floor(rect.width), fallback);
		const height = Math.max(Math.floor(rect.height), fallback);
		canvas.width = width;
		canvas.height = height;
		canvas.style.width = '100%';
		canvas.style.height = '100%';
	}

	private destroyChart(): void {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	}
}
